import { useTranslation } from "react-i18next";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress, Box } from "@mui/material";
import { Button } from "@foodpilot/foods";

interface DeleteConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirmDelete: () => void;
    isDeleting: boolean;
    extensionName: string;
}

export const DeleteConfirmDialog = ({
    open,
    onClose,
    onConfirmDelete,
    isDeleting,
    extensionName,
}: DeleteConfirmDialogProps) => {
    const { t } = useTranslation();

    const handleClose = () => {
        if (!isDeleting) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t("settings.cms.dynablocks.delete_dialog.title", "Delete DynaBlock")}</DialogTitle>

            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    {t(
                        "settings.cms.dynablocks.delete_dialog.message",
                        "Are you sure you want to delete this DynaBlock?",
                    )}
                </Typography>

                <Box
                    sx={{
                        p: 2,
                        mt: 2,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.300",
                    }}
                >
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t("settings.cms.dynablocks.delete_dialog.extension_name", "DynaBlock name:")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                        {extensionName}
                    </Typography>
                </Box>

                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    ⚠️{" "}
                    {t(
                        "settings.cms.dynablocks.delete_dialog.warning",
                        "This action cannot be undone. The DynaBlock will be permanently removed from all pages where it's currently displayed.",
                    )}
                </Typography>
            </DialogContent>

            <DialogActions>
                <Button variant="text" onClick={handleClose} disabled={isDeleting}>
                    {t("common.cancel", "Cancel")}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={onConfirmDelete}
                    disabled={isDeleting}
                    startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
                >
                    {isDeleting ?
                        t("settings.cms.dynablocks.delete_dialog.deleting", "Deleting...")
                    :   t("settings.cms.dynablocks.delete_dialog.delete", "Delete DynaBlock")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
